import { useEffect, useRef } from 'react';
import { ChevronDownIcon, ExternalLinkIcon } from 'lucide-react';
import { ContentEditor, type Json } from './ContentEditor';
import { fieldLabels, humanize } from '../adminLabels';
import { plural } from '../../core/format/text';
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  /** Blok dat open moet (bijv. na een zoekresultaat). */
  focusKey: string | null;
  /** Telt op bij elke zoekkeuze, zodat hetzelfde blok opnieuw kiezen weer opent en scrollt. */
  focusTick?: number;
  /**
   * Vaste opbouw van de pagina (bijv. de homepage): volgorde, titel, rol in het verhaal en een
   * link naar precies dat blok op de site. Blokken die hier niet in staan, komen erachter.
   */
  blocks?: readonly ContentBlock[];
};
export type ContentBlock = {
  key: string;
  title: string;
  role: string;
  href?: string;
};
const isObject = (v: Json): v is {
  [key: string]: Json;
} => typeof v === 'object' && v !== null && !Array.isArray(v);

/** De kop van een blok zoals hij op de site staat, als herkenning in de dichte balk. */
export function previewOf(v: Json): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? plural(v.length, 'item', 'items') : 'Leeg';
  if (isObject(v)) {
    const heading = v.heading;
    if (isObject(heading) && typeof heading.text === 'string' && heading.text) return heading.text;
    for (const key of ['title', 'name', 'eyebrow', 'text', 'body']) {
      const x = v[key];
      if (typeof x === 'string' && x) return x;
    }
  }
  return '';
}

/**
 * Een pagina als rij dichtgeklapte blokken, in de volgorde van de site. Eén lang formulier
 * van tachtig velden is niet te overzien; zo zie je eerst de opbouw en open je alleen het
 * blok dat je wilt aanpassen. Losse tekstregels bovenaan de pagina blijven gewoon open.
 */
export function ContentSections({
  value,
  onChange,
  focusKey,
  focusTick,
  blocks
}: Props) {
  const refs = useRef<Record<string, HTMLDetailsElement | null>>({});
  useEffect(() => {
    if (!focusKey) return;
    const el = refs.current[focusKey];
    if (!el) return;
    el.open = true;
    el.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }, [focusKey, focusTick]);

  // Klantcitaten e.d. zijn één lijst: die heeft geen blokken.
  if (!isObject(value)) return <ContentEditor value={value} onChange={onChange} data-id="element-4971" data-mp="1cbp04j7bhsgo" />;
  const visible = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
  const position = (key: string) => {
    const at = blocks?.findIndex(b => b.key === key) ?? -1;
    return at === -1 ? Number.MAX_SAFE_INTEGER : at;
  };
  const entries = blocks ? [...visible].sort(([a], [b]) => position(a) - position(b)) : visible;
  return <div className="space-y-3" data-id="element-4972" data-mp="1v03qd49t92gf">
      {entries.map(([key, v], i) => {
      const block = blocks?.find(b => b.key === key);
      const number = block ? position(key) + 1 : null;
      const set = (next: Json) => onChange({
        ...value,
        [key]: next
      });
      if (typeof v !== 'object' || v === null) {
        return <div key={key} className="rounded-md border border-line bg-white p-4" data-id="element-4973" data-mp="2a3wrwf2fdkl8">
              <ContentEditor fieldKey={key} value={v} onChange={set} data-id="element-4974" data-mp="2a69ifgm9139w" />
            </div>;
      }
      const preview = previewOf(v);
      return <details key={key} ref={el => {
        refs.current[key] = el;
      }} open={i === 0} className="group scroll-mt-24 rounded-md border border-line bg-white open:shadow-sm" data-id="element-4975" data-mp="214jqm9ogg2bl" data-mpi={i}>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden" data-id="element-4976" data-mp="2o3fy32boeluw">
              {number !== null && <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-shell text-[12px] tabular-nums text-ink" data-id="element-4977" data-mp="2k004yfytf3my">
                  {number}
                </span>}
              <span className="min-w-0 flex-1" data-id="element-4978" data-mp="2wb91ht5kwkku">
                <span className="block text-[14px] font-medium text-ink" data-id="element-4979" data-mp="2wahaywqu9d1f">
                  {block ? <>
                      <span className="sr-only" data-id="element-4980" data-mp="317zda4acou0g">Blok {number}: </span>
                      {block.title}
                      <span className="font-normal text-muted" data-id="element-4981" data-mp="2ncxbntcjdqtg"> · {block.role}</span>
                    </> : humanize(key)}
                </span>
                {preview && <span className="mt-0.5 block truncate text-[12px] text-muted" data-id="element-4982" data-mp="3k0k2nbqucgzi">{preview}</span>}
              </span>
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft group-open:rotate-180" strokeWidth={1.5} aria-hidden="true" data-id="element-4983" data-mp="36a8a5bqcgg7u" />
            </summary>
            <div className="border-t border-line p-4" data-id="element-4984" data-mp="1tduo6emihcy">
              {(fieldLabels[key]?.hint || block?.href) && <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-4985" data-mp="1rc2lvh1pzhco">
                  {fieldLabels[key]?.hint ? <p className="max-w-prose text-[12px] text-muted" data-id="element-4986" data-mp="1me118ronb9z3">{fieldLabels[key]?.hint}</p> : <span data-id="element-4987" data-mp="211vv4bee3kdr" />}
                  {block?.href && <a href={block.href} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-clay underline underline-offset-2 hover:text-ink" data-id="element-4988" data-mp="2148b4j0l7ko6">
                      Bekijk dit blok
                      <ExternalLinkIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-4989" data-mp="1ibxntxif1vb6" />
                      <span className="sr-only" data-id="element-4990" data-mp="1dcba7myoed1u"> (opent in een nieuw tabblad)</span>
                    </a>}
                </div>}
              <ContentEditor fieldKey={key} value={v} onChange={set} depth={0} data-id="element-4991" data-mp="2orqaazm6ll16" />
            </div>
          </details>;
    })}
    </div>;
}